/**
 * Step labels (retest item 7 — "Stepper still anonymous").
 *
 * The stepper rendered anonymous segments plus "n / total"; a customer mid-way
 * through a plan-derived flow had no idea WHAT step 3 of 6 was. This module is
 * the single, pure mapping from a StepId to its customer-facing i18n key —
 * plan-derived check steps resolve through the same `droom.check.*` vocabulary
 * MissingCheckStep already renders (moved here so both consume ONE map), frame
 * steps get their own `droom.step.*` keys, and the pre-stepper entry screens
 * (welcome/schedule) honestly resolve to null — they are not stepper steps.
 */

import type { CheckKey, StepId } from '@/types';
import { checkKeyOfStep, isCheckStepId } from '@/types';

/** Customer-facing label keys for the KNOWN check vocabulary (deck `droom.check.*`). */
export const CHECK_LABEL_KEYS: Partial<Record<CheckKey, string>> = {
  DIGILOCKER_AADHAAR: 'droom.check.DIGILOCKER_AADHAAR',
  PAN_SOURCE: 'droom.check.PAN_SOURCE',
  DOC_CAPTURE: 'droom.check.DOC_CAPTURE',
  CONSENT: 'droom.check.CONSENT',
  FACE_MATCH: 'droom.check.FACE_MATCH',
  LIVENESS: 'droom.check.LIVENESS',
  GEO_IP: 'droom.check.GEO_IP',
  AGENT_QUESTIONS: 'droom.check.AGENT_QUESTIONS',
  AML_SCREENING: 'droom.check.AML_SCREENING',
  SELFIE: 'droom.check.SELFIE',
  SELFIE_WITH_DOC: 'droom.check.SELFIE_WITH_DOC',
};

/** Frame-step label keys (stepper-visible frames only — entry screens are null). */
const FRAME_LABEL_KEYS: Partial<Record<string, string>> = {
  consent: 'droom.step.consent',
  confirm: 'droom.step.confirm',
  // The legacy frame steps carry the same human meaning as their check twins.
  aadhaar: 'droom.check.DIGILOCKER_AADHAAR',
  capture: 'droom.step.capture',
  waiting: 'droom.step.waiting',
  call: 'droom.step.call',
  complete: 'droom.step.complete',
};

/**
 * The i18n key for a step's human label, or null when the step has none
 * (entry screens, or an unknown wire key on a skewed deploy — the caller
 * renders nothing rather than a raw enum at a customer).
 */
export function stepLabelKey(step: StepId): string | null {
  if (isCheckStepId(step)) {
    return CHECK_LABEL_KEYS[checkKeyOfStep(step)] ?? null;
  }
  return FRAME_LABEL_KEYS[step] ?? null;
}
